import { type TouchEvent, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Anime } from "@/mocks/anime";

export default function HeroCarousel({ items }: { items: Anime[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const total = items.length;

  useEffect(() => {
    if (paused || total <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, total]);

  const go = (next: number) => setIndex(((next % total) + total) % total);

  const onTouchStart = (event: TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const onTouchEnd = (event: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      if (delta < 0) go(index + 1);
      else go(index - 1);
    }
    touchStartX.current = null;
  };

  if (!total) return null;

  return (
    <section className="relative w-full" aria-label="焦点推荐">
      <div
        className="relative h-[440px] w-full overflow-hidden md:h-[560px]"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {items.map((item, i) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={item.banner}
              alt={item.title}
              title={`${item.title} - 58动漫焦点推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/50 to-foreground-950/20" />
            <div className="absolute inset-0 flex items-end">
              <div className="w-full px-4 pb-14 md:px-8 md:pb-16 xl:px-12">
                <div className="max-w-2xl">
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
                      精选推荐
                    </span>
                    {item.types.slice(0, 2).map((type) => (
                      <span
                        key={type}
                        className="rounded-full border border-background-50/40 px-3 py-1 text-xs text-background-50/90"
                      >
                        {type}
                      </span>
                    ))}
                    {typeof item.rating === "number" && (
                      <span className="flex items-center gap-1 rounded-full bg-background-50/20 px-3 py-1 text-xs font-semibold text-background-50 backdrop-blur">
                        <i className="ri-star-fill text-primary-300" />
                        {item.rating.toFixed(1)}
                      </span>
                    )}
                  </div>
                  <h2 className="font-heading text-2xl font-black leading-tight text-background-50 md:text-4xl">
                    {item.title}
                  </h2>
                  <p className="mt-3 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-100/90 md:text-base">
                    {item.synopsis}
                  </p>
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <Link
                      to={`/detail/${item.id}`}
                      className="flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-play-circle-line text-base" />
                      查看详情
                    </Link>
                    <span className="flex items-center gap-4 text-xs text-background-100/80">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-star-line" />
                        {item.director}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line" />
                        {item.year} · {item.region}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          aria-label="上一张"
          onClick={() => go(index - 1)}
          className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/25 text-lg text-background-50 backdrop-blur transition-colors hover:bg-primary-500 md:left-6 md:h-11 md:w-11"
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => go(index + 1)}
          className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/25 text-lg text-background-50 backdrop-blur transition-colors hover:bg-primary-500 md:right-6 md:h-11 md:w-11"
        >
          <i className="ri-arrow-right-s-line" />
        </button>

        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到第 ${i + 1} 张`}
              onClick={() => go(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-6 bg-primary-500" : "w-2.5 bg-background-50/50 hover:bg-background-50/80"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}